import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload, FileText, Download, Check, AlertTriangle } from 'lucide-react';
import { useT } from '@/store/i18n';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { toast } from '@/components/ui/Toast';
import { getToken } from '@/lib/api';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface PreviewRow {
  row?: number;
  question_text?: string;
  question_type?: string;
  subject_name?: string;
  grade_name?: string;
  errors?: string[];
}

interface PreviewResponse {
  total_rows?: number;
  valid_rows?: number;
  invalid_rows?: number;
  preview?: PreviewRow[];
  errors?: string[];
}

interface Template {
  nameKey: string;
  file: string;
  type: 'multiple_choice' | 'true_false' | 'matching' | 'multiple_answer' | 'comprehensive' | 'subject';
  descKey?: string;
}

const TEMPLATES: Template[] = [
  // Standard question types
  { nameKey: 'import.tpl.4_choices', file: 'template-4-choices.csv', type: 'multiple_choice', descKey: 'import.tpl.4_choices_desc' },
  { nameKey: 'import.tpl.4_choices_win', file: 'template-4-choices-windows.csv', type: 'multiple_choice', descKey: 'import.tpl.4_choices_win_desc' },
  { nameKey: 'import.tpl.3_choices', file: 'template-3-choices.csv', type: 'multiple_choice', descKey: 'import.tpl.3_choices_desc' },
  { nameKey: 'import.tpl.3_choices_win', file: 'template-3-choices-windows.csv', type: 'multiple_choice' },
  { nameKey: 'import.tpl.true_false', file: 'template-true-false.csv', type: 'true_false', descKey: 'import.tpl.true_false_desc' },
  { nameKey: 'import.tpl.matching', file: 'template-matching.csv', type: 'matching', descKey: 'import.tpl.matching_desc' },
  { nameKey: 'import.tpl.multi_answer', file: 'template-multiple-answer.csv', type: 'multiple_answer', descKey: 'import.tpl.multi_answer_desc' },

  // Subject-specific
  { nameKey: 'import.tpl.math', file: 'mathematics-template.csv', type: 'subject', descKey: 'import.tpl.math_desc' },
  { nameKey: 'import.tpl.math_official', file: 'mathematics-official-template.csv', type: 'subject', descKey: 'import.tpl.math_official_desc' },

  // Comprehensive
  { nameKey: 'import.tpl.comprehensive', file: 'comprehensive-template.csv', type: 'comprehensive', descKey: 'import.tpl.comprehensive_desc' },
];

const TEMPLATE_GROUPS: Array<{ key: Template['type']; labelKey: string }> = [
  { key: 'multiple_choice', labelKey: 'import.group.multiple_choice' },
  { key: 'true_false', labelKey: 'import.group.true_false' },
  { key: 'matching', labelKey: 'import.group.matching' },
  { key: 'multiple_answer', labelKey: 'import.group.multiple_answer' },
  { key: 'subject', labelKey: 'import.group.subject' },
  { key: 'comprehensive', labelKey: 'import.group.comprehensive' },
];

export function ImportQuestionsModal({ open, onClose }: Props) {
  const t = useT();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);

  function reset() {
    setFile(null);
    setPreview(null);
    if (fileRef.current) fileRef.current.value = '';
  }

  const previewMutation = useMutation({
    mutationFn: async (f: File) => {
      const fd = new FormData();
      fd.append('import_file', f);
      const token = getToken();
      const res = await fetch('/api/questions/import-preview.php', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      const json = await res.json();
      if (!res.ok || json.success === false) throw new Error(json.message || t('import.read_failed'));
      return (json.data ?? json) as PreviewResponse;
    },
    onSuccess: (data) => setPreview(data),
    onError: (e: Error) => toast.error(t('import.fail_preview'), e.message),
  });

  const importMutation = useMutation({
    mutationFn: async (f: File) => {
      const fd = new FormData();
      fd.append('import_file', f);
      const token = getToken();
      const res = await fetch('/api/questions/import.php', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      const json = await res.json();
      if (!res.ok || json.success === false) throw new Error(json.message || t('import.fail_import'));
      return json;
    },
    onSuccess: (data) => {
      const count = data?.data?.imported_count ?? data?.imported_count ?? '';
      toast.success(`${t('import.success')}${count ? ` (${count} ${t('units.questions')})` : ''}`);
      qc.invalidateQueries({ queryKey: ['questions'] });
      qc.invalidateQueries({ queryKey: ['dashboard-stats'] });
      reset();
      onClose();
    },
    onError: (e: Error) => toast.error(t('import.fail_import'), e.message),
  });

  function chooseFile(f: File) {
    setFile(f);
    setPreview(null);
    previewMutation.mutate(f);
  }

  return (
    <Modal
      open={open}
      onClose={() => { reset(); onClose(); }}
      title={t('import.title')}
      description={t('import.desc')}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={() => { reset(); onClose(); }}>{t('btn.cancel')}</Button>
          <Button
            disabled={!file || !!(preview && (preview.invalid_rows ?? 0) > 0 && (preview.valid_rows ?? 0) === 0)}
            loading={importMutation.isPending}
            onClick={() => file && importMutation.mutate(file)}
            leftIcon={<Upload className="w-4 h-4" />}
          >
            {t('import.do_import')} {preview?.valid_rows ? `${preview.valid_rows} ${t('units.questions')}` : ''}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {/* Templates — grouped by question type, matching v7's 13 templates */}
        <div className="rounded-xl bg-blue-50 border border-blue-100 p-4">
          <div className="flex items-start gap-2 mb-3">
            <FileText className="w-4 h-4 text-blue-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-blue-900">{t('import.template_title')}</h4>
              <p className="text-xs text-blue-700">{t('import.template_desc')}</p>
            </div>
          </div>
          <div className="space-y-3">
            {TEMPLATE_GROUPS.map((g) => {
              const items = TEMPLATES.filter((tpl) => tpl.type === g.key);
              if (items.length === 0) return null;
              return (
                <div key={g.key}>
                  <div className="text-[11px] font-semibold text-blue-700 mb-1.5 uppercase tracking-wider">
                    {t(g.labelKey)}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {items.map((tpl) => (
                      <a
                        key={tpl.file}
                        href={`/import-templates/${tpl.file}`}
                        download
                        title={tpl.descKey ? t(tpl.descKey) : undefined}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-white border border-blue-200 rounded-md text-blue-700 hover:bg-blue-100"
                      >
                        <Download className="w-3 h-3" />
                        {t(tpl.nameKey)}
                      </a>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Upload */}
        {!file ? (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="w-full border-2 border-dashed border-slate-300 rounded-lg px-4 py-10 flex flex-col items-center gap-2 text-slate-500 hover:border-brand-400 hover:bg-brand-50/30 transition-colors"
          >
            <Upload className="w-8 h-8" />
            <span className="text-sm font-medium">{t('import.click_to_select')}</span>
            <span className="text-xs text-slate-400">{t('import.file_hint')}</span>
          </button>
        ) : (
          <div className="rounded-lg border border-slate-200 p-4 flex items-center gap-3">
            <FileText className="w-5 h-5 text-slate-500" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">{file.name}</div>
              <div className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB</div>
            </div>
            <button type="button" onClick={reset} className="text-xs text-red-600 hover:underline">{t('import.change_file')}</button>
          </div>
        )}

        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) chooseFile(f);
          }}
        />

        {/* Preview */}
        {previewMutation.isPending && <Skeleton className="h-24 w-full" />}
        {preview && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                <div className="text-xl font-bold text-slate-900">{preview.total_rows ?? 0}</div>
                <div className="text-xs text-slate-500">{t('import.all_rows')}</div>
              </div>
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
                <div className="text-xl font-bold text-emerald-700">{preview.valid_rows ?? 0}</div>
                <div className="text-xs text-emerald-600">{t('import.valid_rows')}</div>
              </div>
              <div className="rounded-lg bg-red-50 border border-red-200 p-3">
                <div className="text-xl font-bold text-red-700">{preview.invalid_rows ?? 0}</div>
                <div className="text-xs text-red-600">{t('import.invalid_rows')}</div>
              </div>
            </div>
            {(preview.preview?.length ?? 0) > 0 && (
              <ul className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-60 overflow-y-auto">
                {preview.preview!.slice(0, 10).map((r, i) => {
                  const hasErr = (r.errors?.length ?? 0) > 0;
                  return (
                    <li key={i} className="px-3 py-2 flex items-start gap-2 text-sm">
                      {hasErr ? (
                        <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                      ) : (
                        <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-slate-500">{t('import.row')} {r.row ?? i + 1}</div>
                        <div className="text-slate-900 truncate">{r.question_text ?? '—'}</div>
                        {r.subject_name && <Badge tone="brand" className="mt-1">{r.subject_name}</Badge>}
                        {hasErr && (
                          <ul className="text-xs text-red-600 mt-1 list-disc pl-4">
                            {r.errors!.map((err, j) => <li key={j}>{err}</li>)}
                          </ul>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
